import { AbstractDataProvider } from "./AbstractDataProvider";
import { PostalAddress } from "../model/PostalAddress";
import { GetListParams, GetListResult, GetOneParams, GetOneResult } from "react-admin";
import { applyFilter, applyPagination } from "../backoffice.utils";
import { drivingSchoolsProvider } from "./drivingSchoolsProvider";
import { isStudent } from "./studentsProvider";
import { collection, getDocs, query } from "firebase/firestore";
import { firestore } from "../firebase";
import { type ExamLocationWeekday } from "../model/ExamLocation";
import { FavoriteLocationSchema } from "../model/FavoriteLocation";
import { DrivingLessonTypeEnum } from "../model/DrivingLessonType";
import { examLocationsProvider } from "./examLocationsProvider";

export interface StartingPoint {
  id: string;
  name: string;
  postalAddress: PostalAddress;
}

class StartingPointsProvider extends AbstractDataProvider<StartingPoint> {
  private _cache = new Map<string, StartingPoint>();

  async getOne(_: string, { id, meta }: GetOneParams<StartingPoint>): Promise<GetOneResult<StartingPoint>> {
    let startingPoint = this.getOneFromCache(id);
    if (startingPoint) {
      return { data: startingPoint };
    }
    if (meta) {
      await this.getList("startingPoints", {
        filter: {},
        sort: { field: "__apply_special_starting_point_sort_logic__", order: "ASC" },
        pagination: { page: 1, perPage: 999 },
        meta,
      });
      startingPoint = this.getOneFromCache(id);
      if (startingPoint) {
        return { data: startingPoint };
      }
    }
    throw new Error(`Starting point with id "${id}" not found in cache`);
  }

  getOneFromCache(id: string): StartingPoint | undefined {
    return this._cache.get(id);
  }

  async getList(_: string, params: GetListParams): Promise<GetListResult<StartingPoint>> {
    const { filter, pagination, sort } = params;
    const meta = params?.meta ?? {};
    if (!("student" in meta)) {
      throw new Error("Missing student property in meta");
    }
    const student = meta?.student;
    if (!isStudent(student)) {
      throw new Error("meta.student is not a Student");
    }
    const drivingLessonType = meta?.drivingLessonType;
    if (!drivingLessonType) {
      throw new Error("Missing drivingLessonType property in meta");
    } else if (!DrivingLessonTypeEnum.safeParse(drivingLessonType).success) {
      throw new Error(`Invalid meta.drivingLessonType: ${JSON.stringify(drivingLessonType)}`);
    }
    if (sort.field !== "__apply_special_starting_point_sort_logic__" || sort.order !== "ASC") {
      throw new Error(
        `Unexpected sort: ${JSON.stringify(
          sort,
        )} -- expected: {field: "__apply_special_starting_point_sort_logic__", order: "ASC"}`,
      );
    }

    const startingPoints: Array<StartingPoint> = [];

    if (drivingLessonType === "theoretischePruefung") {
      const { data: examLocations } = await examLocationsProvider.getList("examLocations", {
        filter: {
          drivingSchoolId: student.drivingSchoolId,
          theoryExamWeekdays: (it: undefined | Array<ExamLocationWeekday>) => it?.length,
        },
        pagination: { page: 1, perPage: 999 },
        sort: { field: "name", order: "ASC" },
        meta: { drivingLessonType, student },
      });
      for (const examLocation of examLocations) {
        startingPoints.push({
          id: `exam location ${examLocation.id}`,
          name: examLocation.name,
          postalAddress: examLocation.postalAddress,
        });
      }
    } else if (drivingLessonType === "praktischePruefung") {
      // Add exam location(s) ...
      const { data: examLocations } = await examLocationsProvider.getList("examLocations", {
        filter: {
          drivingSchoolId: student.drivingSchoolId,
          practicalExamWeekdays: (it: undefined | Array<ExamLocationWeekday>) => it?.length,
        },
        pagination: { page: 1, perPage: 999 },
        sort: { field: "name", order: "ASC" },
        meta: { drivingLessonType, student },
      });
      for (const examLocation of examLocations) {
        startingPoints.push({
          id: `exam location ${examLocation.id}`,
          name: examLocation.name,
          postalAddress: examLocation.postalAddress,
        });
      }
    }

    const instructorIds =
      meta.instructorId && meta.instructorId !== "__DO_NOT_CHANGE__" ? [meta.instructorId] : student.instructorIds;

    for (const instructorId of instructorIds) {
      // Add favorite locations of instructor ...
      const snapshot = await getDocs(query(collection(firestore, `/users/${instructorId}/favorite_locations`)));
      for (const doc of snapshot.docs) {
        const parseResult = FavoriteLocationSchema.safeParse(doc.data());
        if (parseResult.success) {
          const favoriteLocation = parseResult.data;
          startingPoints.push({
            id: `favorite location ${doc.id}`,
            name: favoriteLocation.name,
            postalAddress: favoriteLocation.postalAddress,
          });
        } else {
          console.warn(`Failed to parse ${doc.ref.path} -- ignoring it`, parseResult.error.errors);
        }
      }
    }

    const { data: drivingSchool } = await drivingSchoolsProvider.getOne("drivingSchools", {
      id: student.drivingSchoolId,
    });
    // Add branch address(es) ...
    for (const branch of drivingSchool.branches) {
      startingPoints.push({
        id: `branch ${branch.id}`,
        name: branch.name,
        postalAddress: branch.postalAddress,
      });
    }
    // Add student address ...
    if (drivingSchool.customizations.includes("pickupFromHomeAddress")) {
      startingPoints.push({
        id: `home of student ${student.id}`,
        name: `Wohnsitz von ${student.name}`,
        postalAddress: student.postalAddress,
      });
    }

    this._cache.clear();
    for (const startingPoint of startingPoints) {
      this._cache.set(startingPoint.id, startingPoint);
    }
    return applyPagination(applyFilter(startingPoints, filter), pagination);
  }
}

export const startingPointsProvider = new StartingPointsProvider();
